import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import AdminBackButton from '../components/AdminBackButton';
import '../components/AdminLayout.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface Category {
  id: string;
  name: string;
  createdAt: string;
}

interface Asset {
  id: string;
  name: string;
  description: string | null;
  assetType: 'video' | 'image' | 'audio';
  fileUrl: string;
  mimeType: string;
  fileSize: number;
  duration: number | null;
  thumbnailUrl: string | null;
  categoryIds: string[];
  createdAt: string;
}

type UploadMode = 'single' | 'bulk';
type UploadType = 'video' | 'image' | 'gif' | 'audio';

const UPLOAD_ACCEPT_BY_TYPE: Record<UploadType, string> = {
  video: 'video/mp4,video/quicktime,video/webm',
  image: 'image/jpeg,image/png,image/webp',
  gif: 'image/gif',
  audio: 'audio/mpeg,audio/wav,audio/mp4,audio/aac,audio/ogg,audio/x-wav,audio/x-m4a,audio/m4a',
};

const UPLOAD_MIME_BY_TYPE: Record<UploadType, Set<string>> = {
  video: new Set(['video/mp4', 'video/quicktime', 'video/webm']),
  image: new Set(['image/jpeg', 'image/png', 'image/webp']),
  gif: new Set(['image/gif']),
  audio: new Set(['audio/mpeg', 'audio/wav', 'audio/mp4', 'audio/aac', 'audio/ogg', 'audio/x-wav', 'audio/x-m4a', 'audio/m4a']),
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function renderAssetPreview(asset: Asset): React.ReactNode {
  if (asset.assetType === 'image') {
    return <img src={asset.fileUrl} alt={asset.name} className="admin-asset-preview-image" />;
  }

  if (asset.assetType === 'video') {
    return (
      <video className="admin-asset-preview-video" controls preload="metadata" src={asset.fileUrl}>
        Your browser does not support inline video playback.
      </video>
    );
  }

  if (asset.assetType === 'audio') {
    return (
      <audio className="admin-asset-preview-audio" controls preload="metadata" src={asset.fileUrl}>
        Your browser does not support inline audio playback.
      </audio>
    );
  }

  return <div className="admin-asset-preview-empty">Preview not available for this file type.</div>;
}

const AdminAssets: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadCategoryIds, setUploadCategoryIds] = useState<string[]>([]);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadMode, setUploadMode] = useState<UploadMode>('single');
  const [uploadType, setUploadType] = useState<UploadType>('image');
  const [uploadName, setUploadName] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null);
  const [editAssetName, setEditAssetName] = useState('');
  const [editAssetDescription, setEditAssetDescription] = useState('');
  const [editCategoryIds, setEditCategoryIds] = useState<string[]>([]);
  const [savingAssetEdit, setSavingAssetEdit] = useState(false);
  const [previewAsset, setPreviewAsset] = useState<Asset | null>(null);

  const loadAll = () => {
    setLoading(true);
    const params = new URLSearchParams({ limit: '50' });
    if (filterType) params.set('type', filterType);
    if (filterCategory) params.set('categoryId', filterCategory);
    Promise.all([
      fetch(`${API_BASE}/api/admin/asset-categories`, { credentials: 'include' }).then(r => r.json()),
      fetch(`${API_BASE}/api/admin/assets?${params}`, { credentials: 'include' }).then(r => r.json()),
    ]).then(([catData, assetData]) => {
      setCategories(catData.categories || []);
      setAssets(assetData.assets || []);
      setTotal(assetData.total || 0);
    }).catch(() => setError('Failed to load assets'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadAll(); }, [filterType, filterCategory]);

  useEffect(() => {
    if (!previewAsset && !isUploadModalOpen && !editingAsset) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (previewAsset) {
          setPreviewAsset(null);
          return;
        }
        if (editingAsset && !savingAssetEdit) {
          setEditingAsset(null);
          return;
        }
        if (!uploading) {
          setIsUploadModalOpen(false);
        }
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [editingAsset, isUploadModalOpen, previewAsset, savingAssetEdit, uploading]);

  const resetUploadForm = () => {
    setUploadMode('single');
    setUploadType('image');
    setUploadName('');
    setUploadDescription('');
    setUploadFile(null);
    setUploadCategoryIds([]);
  };

  const openUploadModal = () => {
    setError(null);
    setIsUploadModalOpen(true);
  };

  const closeUploadModal = () => {
    if (uploading) return;
    setIsUploadModalOpen(false);
    resetUploadForm();
  };

  const openEditAssetModal = (asset: Asset) => {
    setError(null);
    setEditingAsset(asset);
    setEditAssetName(asset.name);
    setEditAssetDescription(asset.description ?? '');
    setEditCategoryIds(asset.categoryIds ?? []);
  };

  const closeEditAssetModal = () => {
    if (savingAssetEdit) return;
    setEditingAsset(null);
    setEditAssetName('');
    setEditAssetDescription('');
    setEditCategoryIds([]);
  };

  const createCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/asset-categories`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newCategoryName.trim() }),
      });
      if (!res.ok) throw new Error();
      const cat = await res.json();
      setCategories(prev => [...prev, cat]);
      setNewCategoryName('');
    } catch { setError('Failed to create category'); }
  };

  const deleteCategory = async (id: string) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await fetch(`${API_BASE}/api/admin/asset-categories/${id}`, { method: 'DELETE', credentials: 'include' });
      setCategories(prev => prev.filter(c => c.id !== id));
      setUploadCategoryIds(prev => prev.filter(categoryId => categoryId !== id));
      setEditCategoryIds(prev => prev.filter(categoryId => categoryId !== id));
    } catch { setError('Failed to delete category'); }
  };

  const selectUploadFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setUploadFile(file);
    if (file && !uploadName.trim()) {
      setUploadName(file.name.replace(/\.[^.]+$/, ''));
    }
  };

  const submitSingleUpload = async () => {
    if (!uploadFile) {
      setError('Please select a file to upload');
      return;
    }

    if (!UPLOAD_MIME_BY_TYPE[uploadType].has(uploadFile.type)) {
      setError(`Selected file does not match ${uploadType.toUpperCase()} upload type`);
      return;
    }

    const normalizedName = uploadName.trim() || uploadFile.name;
    if (!normalizedName) {
      setError('Please provide a media name');
      return;
    }

    setUploading(true); setError(null);
    const form = new FormData();
    form.append('file', uploadFile);
    form.append('name', normalizedName);
    form.append('description', uploadDescription.trim());
    form.append('uploadType', uploadType);
    form.append('categoryIds', JSON.stringify(uploadCategoryIds));

    try {
      const res = await fetch(`${API_BASE}/api/admin/assets`, {
        method: 'POST', credentials: 'include', body: form,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error || 'Upload failed');
      }

      loadAll();
      setIsUploadModalOpen(false);
      resetUploadForm();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const submitAssetMetadataUpdate = async () => {
    if (!editingAsset) return;

    const normalizedName = editAssetName.trim();
    if (!normalizedName) {
      setError('Asset name is required');
      return;
    }

    if (normalizedName.length > 255) {
      setError('Asset name must be 255 characters or less');
      return;
    }

    const normalizedDescription = editAssetDescription.trim();
    if (normalizedDescription.length > 5000) {
      setError('Description must be 5000 characters or less');
      return;
    }

    setSavingAssetEdit(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/api/admin/assets/${editingAsset.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: normalizedName,
          description: normalizedDescription || null,
          categoryIds: editCategoryIds,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error || 'Failed to update asset');
      }

      const updated = await res.json() as Asset;
      setAssets(prev => prev.map(asset => (asset.id === updated.id ? updated : asset)));
      setPreviewAsset(prev => (prev?.id === updated.id ? updated : prev));
      setEditingAsset(null);
      setEditAssetName('');
      setEditAssetDescription('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update asset');
    } finally {
      setSavingAssetEdit(false);
    }
  };

  const deleteAsset = async (id: string) => {
    if (!window.confirm('Delete this asset?')) return;
    try {
      await fetch(`${API_BASE}/api/admin/assets/${id}`, { method: 'DELETE', credentials: 'include' });
      setAssets(prev => prev.filter(a => a.id !== id));
      setPreviewAsset(prev => (prev?.id === id ? null : prev));
      setEditingAsset(prev => (prev?.id === id ? null : prev));
      setTotal(prev => prev - 1);
    } catch { setError('Failed to delete asset'); }
  };

  return (
    <AdminLayout>
      <div className="admin-page-header admin-page-header--back admin-page-header-row">
        <div>
          <div className="admin-page-title-row">
            <AdminBackButton />
            <h1 className="admin-page-title">Assets</h1>
          </div>
          <p className="admin-page-subtitle admin-page-subtitle--data">{total} asset(s) total</p>
        </div>
        <div className="admin-row admin-gap-8">
          <button className="admin-btn admin-btn-primary" disabled={uploading}
            onClick={openUploadModal}>
            {uploading ? 'Uploading…' : '+ Upload Asset'}
          </button>
        </div>
      </div>

      {error && <div className="admin-error">{error}</div>}

      {/* Categories */}
      <div className="admin-card admin-card-spaced">
        <h3 className="admin-section-title">Categories</h3>
        <div className="admin-wrap-row admin-mb-12">
          {categories.map(c => (
            <div key={c.id} className="admin-category-chip">
              <span className="admin-category-chip-name">{c.name}</span>
              <button onClick={() => deleteCategory(c.id)} className="admin-category-chip-remove">×</button>
            </div>
          ))}
        </div>
        <div className="admin-row admin-gap-8">
          <input className="admin-input admin-input-category" placeholder="New category name…" value={newCategoryName}
            onChange={e => setNewCategoryName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && createCategory()} />
          <button className="admin-btn admin-btn-secondary" onClick={createCategory}>Add</button>
        </div>
      </div>

      {/* Filters */}
      <div className="admin-filters-row">
        <select className="admin-input" value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">All Types</option>
          <option value="image">Images</option>
          <option value="video">Videos</option>
          <option value="audio">Audio</option>
        </select>
        <select className="admin-input" value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
          <option value="">All Categories</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="admin-loading">Loading…</div>
      ) : (
        <div className="admin-card">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Type</th>
                <th>MIME</th>
                <th>Size</th>
                <th>Duration</th>
                <th>Categories</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {assets.length === 0 && (
                <tr><td colSpan={8} className="admin-cell-empty">No assets found.</td></tr>
              )}
              {assets.map(a => (
                <tr key={a.id}>
                  <td>
                    <div className="admin-asset-row">
                      {a.thumbnailUrl && (
                        <img src={a.thumbnailUrl} alt="" className="admin-asset-thumb" />
                      )}
                      <div>
                        <button
                          type="button"
                          className="admin-asset-name admin-asset-name-btn"
                          onClick={() => setPreviewAsset(a)}
                        >
                          {a.name}
                        </button>
                        <button
                          type="button"
                          className="admin-asset-link admin-asset-link-btn"
                          onClick={() => setPreviewAsset(a)}
                        >
                          View file
                        </button>
                      </div>
                    </div>
                  </td>
                  <td className="admin-asset-description-cell">
                    {a.description ? (
                      <span className="admin-asset-description">{a.description}</span>
                    ) : (
                      <span className="admin-cell-muted">—</span>
                    )}
                  </td>
                  <td><span className="admin-badge admin-badge-active admin-badge-capitalize">{a.assetType}</span></td>
                  <td className="admin-cell-mono">{a.mimeType}</td>
                  <td className="admin-cell-small">{formatBytes(a.fileSize)}</td>
                  <td className="admin-cell-muted">
                    {a.duration != null ? `${a.duration.toFixed(1)}s` : '—'}
                  </td>
                  <td className="admin-cell-muted">
                    {a.categoryIds.map(cid => categories.find(c => c.id === cid)?.name).filter(Boolean).join(', ') || '—'}
                  </td>
                  <td>
                    <div className="admin-row admin-gap-8">
                      <button className="admin-btn admin-btn-secondary" onClick={() => openEditAssetModal(a)}>Edit</button>
                      <button className="admin-btn admin-btn-danger" onClick={() => deleteAsset(a.id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editingAsset && (
        <div className="admin-modal-overlay" onClick={e => e.target === e.currentTarget && closeEditAssetModal()}>
          <div className="admin-modal admin-edit-asset-modal" role="dialog" aria-modal="true" aria-label="Edit asset metadata">
            <h2 className="admin-modal-title">Edit Asset</h2>
            <div className="admin-stack admin-stack-gap-12">
              <div>
                <label className="admin-field-label admin-field-label-strong" htmlFor="admin-edit-asset-name">Name</label>
                <input
                  id="admin-edit-asset-name"
                  className="admin-input admin-input-full"
                  value={editAssetName}
                  onChange={event => setEditAssetName(event.target.value)}
                  disabled={savingAssetEdit}
                />
              </div>
              <div>
                <label className="admin-field-label admin-field-label-strong" htmlFor="admin-edit-asset-description">Description</label>
                <textarea
                  id="admin-edit-asset-description"
                  className="admin-input admin-input-full admin-upload-description"
                  value={editAssetDescription}
                  onChange={event => setEditAssetDescription(event.target.value)}
                  placeholder="Describe this media content"
                  disabled={savingAssetEdit}
                />
              </div>
              <div>
                <label className="admin-field-label admin-field-label-strong" htmlFor="admin-edit-asset-categories">Categories</label>
                {categories.length > 0 ? (
                  <>
                    <p className="admin-upload-category-note">Select one or more categories</p>
                    <select
                      id="admin-edit-asset-categories"
                      className="admin-input admin-input-full admin-select-multi"
                      multiple
                      value={editCategoryIds}
                      disabled={savingAssetEdit}
                      onChange={(event) => {
                        const selected = Array.from(event.target.selectedOptions).map((option) => option.value);
                        setEditCategoryIds(selected);
                      }}
                    >
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>{category.name}</option>
                      ))}
                    </select>
                  </>
                ) : (
                  <p className="admin-upload-category-note">No categories available. Create categories above.</p>
                )}
              </div>
            </div>
            <div className="admin-modal-actions">
              <button type="button" className="admin-btn admin-btn-secondary" onClick={closeEditAssetModal} disabled={savingAssetEdit}>
                Cancel
              </button>
              <button type="button" className="admin-btn admin-btn-primary" onClick={submitAssetMetadataUpdate} disabled={savingAssetEdit}>
                {savingAssetEdit ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isUploadModalOpen && (
        <div className="admin-modal-overlay" onClick={e => e.target === e.currentTarget && closeUploadModal()}>
          <div className="admin-modal admin-upload-modal" role="dialog" aria-modal="true" aria-label="Upload asset">
            <div className="admin-upload-modal-header">
              <h2 className="admin-modal-title admin-upload-modal-title">Upload Asset</h2>
              <button
                type="button"
                className="admin-btn admin-btn-secondary admin-btn-compact"
                onClick={closeUploadModal}
                disabled={uploading}
              >
                Close
              </button>
            </div>

            <div className="admin-upload-mode-row">
              <button
                type="button"
                className={`admin-upload-mode-btn ${uploadMode === 'single' ? 'active' : ''}`}
                onClick={() => setUploadMode('single')}
                disabled={uploading}
              >
                Single Upload
              </button>
              <button
                type="button"
                className={`admin-upload-mode-btn ${uploadMode === 'bulk' ? 'active' : ''}`}
                onClick={() => setUploadMode('bulk')}
                disabled={uploading}
              >
                Bulk Upload
              </button>
            </div>

            {uploadMode === 'single' ? (
              <>
                <div className="admin-stack admin-stack-gap-12">
                  <div>
                    <label className="admin-field-label admin-field-label-strong" htmlFor="admin-upload-type">Upload Type</label>
                    <select
                      id="admin-upload-type"
                      className="admin-input admin-input-full"
                      value={uploadType}
                      disabled={uploading}
                      onChange={(event) => {
                        setUploadType(event.target.value as UploadType);
                        setUploadFile(null);
                      }}
                    >
                      <option value="audio">Audio</option>
                      <option value="video">Video</option>
                      <option value="image">Image</option>
                      <option value="gif">GIF</option>
                    </select>
                    <p className="admin-upload-file-note">Select a file that matches the chosen type.</p>
                  </div>

                  <div>
                    <label className="admin-field-label admin-field-label-strong" htmlFor="admin-upload-name">Media Name</label>
                    <input
                      id="admin-upload-name"
                      className="admin-input admin-input-full"
                      value={uploadName}
                      disabled={uploading}
                      onChange={event => setUploadName(event.target.value)}
                      placeholder="Enter media name"
                    />
                  </div>

                  <div>
                    <label className="admin-field-label admin-field-label-strong" htmlFor="admin-upload-description">Media Description</label>
                    <textarea
                      id="admin-upload-description"
                      className="admin-input admin-input-full admin-upload-description"
                      value={uploadDescription}
                      disabled={uploading}
                      onChange={event => setUploadDescription(event.target.value)}
                      placeholder="Describe this media content"
                    />
                  </div>

                  <div>
                    <label className="admin-field-label admin-field-label-strong" htmlFor="admin-upload-file">File</label>
                    <input
                      id="admin-upload-file"
                      type="file"
                      className="admin-input admin-input-full"
                      accept={UPLOAD_ACCEPT_BY_TYPE[uploadType]}
                      disabled={uploading}
                      onChange={selectUploadFile}
                    />
                    {uploadFile && (
                      <p className="admin-upload-file-note">Selected: {uploadFile.name}</p>
                    )}
                  </div>

                  <div>
                    <label className="admin-field-label admin-field-label-strong" htmlFor="admin-upload-categories">Categories</label>
                    {categories.length > 0 ? (
                      <>
                        <p className="admin-upload-category-note">Select one or more categories (optional)</p>
                        <select
                          id="admin-upload-categories"
                          className="admin-input admin-input-full admin-select-multi"
                          multiple
                          value={uploadCategoryIds}
                          disabled={uploading}
                          onChange={(event) => {
                            const selected = Array.from(event.target.selectedOptions).map((option) => option.value);
                            setUploadCategoryIds(selected);
                          }}
                        >
                          {categories.map((category) => (
                            <option key={category.id} value={category.id}>{category.name}</option>
                          ))}
                        </select>
                      </>
                    ) : (
                      <p className="admin-upload-category-note">No categories available. Create categories above.</p>
                    )}
                  </div>
                </div>

                <div className="admin-modal-actions">
                  <button type="button" className="admin-btn admin-btn-secondary" onClick={closeUploadModal} disabled={uploading}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="admin-btn admin-btn-primary"
                    onClick={submitSingleUpload}
                    disabled={uploading || !uploadFile}
                  >
                    {uploading ? 'Uploading…' : 'Upload'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="admin-upload-disabled-panel">
                  <p className="admin-upload-disabled-title">Bulk upload is currently disabled</p>
                  <p className="admin-upload-disabled-copy">Bulk upload flow will be added in a later update.</p>
                </div>
                <div className="admin-modal-actions">
                  <button type="button" className="admin-btn admin-btn-secondary" onClick={closeUploadModal} disabled={uploading}>
                    Close
                  </button>
                  <button type="button" className="admin-btn admin-btn-primary" disabled>
                    Bulk Upload (Disabled)
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {previewAsset && (
        <div className="admin-modal-overlay" onClick={e => e.target === e.currentTarget && setPreviewAsset(null)}>
          <div className="admin-modal admin-asset-preview-modal" role="dialog" aria-modal="true" aria-label={`Preview ${previewAsset.name}`}>
            <div className="admin-asset-preview-header">
              <h2 className="admin-modal-title admin-asset-preview-title">{previewAsset.name}</h2>
              <button
                type="button"
                className="admin-btn admin-btn-secondary admin-btn-compact"
                onClick={() => setPreviewAsset(null)}
              >
                Close
              </button>
            </div>

            <div className="admin-asset-preview-meta">
              <span>{previewAsset.assetType}</span>
              <span className="admin-asset-preview-meta-sep">•</span>
              <span>{previewAsset.mimeType}</span>
              <span className="admin-asset-preview-meta-sep">•</span>
              <span>{formatBytes(previewAsset.fileSize)}</span>
              {previewAsset.duration != null && (
                <>
                  <span className="admin-asset-preview-meta-sep">•</span>
                  <span>{previewAsset.duration.toFixed(1)}s</span>
                </>
              )}
            </div>

            {previewAsset.description && (
              <p className="admin-asset-preview-description">{previewAsset.description}</p>
            )}

            <div className="admin-asset-preview-frame">
              {renderAssetPreview(previewAsset)}
            </div>

            <div className="admin-modal-actions">
              <a
                href={previewAsset.fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="admin-btn admin-btn-secondary admin-asset-preview-link"
              >
                Open in new tab ↗
              </a>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminAssets;
